import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "react-simple-snackbar";
import { PreparationOrder, Shipment, ShippingRecord } from "$types";
import CancellablePromise from "$utils/CancellablePromise";
import useAuth from "./useAuth";
import useMedia from "./useMedia";
import { queryClient } from "$utils/queryClient";
import { getOps } from "$services/Op.service";
import { ShippingRecordService } from "$services";
import {
  snackbarSuccessOptions,
  snackbarErrorOptions,
  MESSAGE_DELETE_OK,
  MESSAGE_DELETE_FAILED,
} from "$utils/SnackbarOptions";
import { CacheInvalidationAction } from "$utils/cache";

const useShippingRecord = (shippingRecordId?: string, shippingRecord?: any) => {
  const { token } = useAuth();
  const [ops, setOps] = useState([]);

  // Fetching
  const [shippingRecordItem, setShippingRecord] = useState(shippingRecord);
  const [isFetchingShippingRecord, setIsFetchingShippingRecord] =
    useState(false);
  const [deleteSnack, setDeleteSnack] = useState(true);
  const [openSnackbar] = useSnackbar(
    deleteSnack ? snackbarSuccessOptions : snackbarErrorOptions
  );

  const fetchShippingRecord = useCallback(
    async (id: string, options?: { force?: boolean }) => {
      const queryKey = [
        CacheInvalidationAction.GET_SHIPPING_RECORD,
        shippingRecordId ?? id,
      ];
      try {
        if (options?.force) {
          await queryClient.invalidateQueries(queryKey);
        }

        let shippingRecordItem = await queryClient.getQueryData(queryKey);
        if (!shippingRecordItem || options?.force) {
          setIsFetchingShippingRecord(true);
          shippingRecordItem = await queryClient.fetchQuery(
            queryKey,
            () =>
              ShippingRecordService.getShippingRecord(
                token,
                shippingRecordId ?? id
              ),
            { cacheTime: 5 * 60 * 1000 }
          );
          await queryClient.setQueryData(queryKey, shippingRecordItem);
        }
        setShippingRecord(shippingRecordItem);
      } catch (error: any) {
        console.error("Error fetching shipping record:", error);
      } finally {
        setIsFetchingShippingRecord(false);
      }
    },
    [setShippingRecord, setIsFetchingShippingRecord, shippingRecordId, token]
  );

  useEffect(() => {
    if (!shippingRecordItem) {
      fetchShippingRecord(shippingRecordId);
    }
  }, [fetchShippingRecord, shippingRecordId, shippingRecordItem]);

  // Update
  const updateShippingRecord = useCallback(
    async (shippingRecordId: string, data: any) => {
      const result = await ShippingRecordService.updateShippingRecord(
        shippingRecordId,
        data
      );
      setShippingRecord(result);
    },
    [setShippingRecord]
  );

  // Create
  const createShippingRecord = useCallback(
    async (data: any) => {
      try {
        const result = await ShippingRecordService.createShippingRecord(data);
        setShippingRecord(result);
      } catch (error: any) {
        openSnackbar(error.message || "Erreur lors de la création du dossier");
      }
    },
    [setShippingRecord, openSnackbar]
  );

  const deleteShippingRecord = useCallback(
    async (id: string) => {
      try {
        await ShippingRecordService.deleteShippingRecord(token, id);
        setDeleteSnack(true);
        openSnackbar(MESSAGE_DELETE_OK);
      } catch (error: any) {
        setDeleteSnack(false);
        openSnackbar(MESSAGE_DELETE_FAILED, error.message);
      }
    },
    [openSnackbar, token]
  );

  const [media, setMedia] = useState<any[]>([]);
  const { getMedia } = useMedia("shipping");

  const fetchFiles = useCallback(async () => {
    if (shippingRecordItem && shippingRecordItem.files) {
      const filesFromApi = await Promise.all(
        shippingRecordItem.files
          .filter((file) => file.mainApiMediaId)
          .map((file) => getMedia(file.mainApiMediaId))
      );
      setMedia(filesFromApi);
    }
  }, [shippingRecordItem, getMedia, setMedia]);

  const fetchOps = useCallback(async () => {
    const opList = shippingRecordItem?.shipments?.map(
      (shipment: Shipment) => shipment.opId
    );
    if (opList?.length > 0) {
      let selectedOps;
      selectedOps = await queryClient.getQueryData([
        CacheInvalidationAction.GET_OPS,
        opList,
      ]);

      if (!selectedOps) {
        selectedOps = await queryClient.fetchQuery(
          [CacheInvalidationAction.GET_OPS, opList],
          () =>
            getOps({
              $inId: opList,
            }),
          { cacheTime: 5 * 60 * 1000 }
        );
        selectedOps = selectedOps.data;

        selectedOps = await queryClient.setQueryData(
          [CacheInvalidationAction.GET_OPS, opList],
          selectedOps
        );
      }
      setOps(selectedOps);
    }
  }, [shippingRecordItem]);

  useEffect(() => {
    fetchOps();
  }, [fetchOps]);

  useEffect(() => {
    if (shippingRecordItem) {
      fetchFiles();
    }
  }, [shippingRecordItem, fetchFiles]);

  const populatedShippingRecord = useMemo(
    () =>
      shippingRecordItem && ops
        ? {
            ...shippingRecordItem,
            shipments: shippingRecordItem.shipments?.map(
              (shipment: Shipment) => ({
                ...shipment,
                op: ops?.find(
                  (op: PreparationOrder) => op._id === shipment.opId
                ),
              })
            ),
            media,
          }
        : null,
    [shippingRecordItem, media, ops]
  );

  return {
    shippingRecord: populatedShippingRecord as ShippingRecord,
    createShippingRecord: createShippingRecord as (
      payload: Partial<ShippingRecord>
    ) => void | CancellablePromise<unknown>,
    updateShippingRecord: updateShippingRecord as (
      shippingRecordId: string,
      data: any
    ) => any,
    deleteShippingRecord,
    fetchShippingRecord,
    isFetchingShippingRecord,
  };
};

export default useShippingRecord;
